import { useState, useEffect, useRef, useMemo } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { BACKEND_URL } from '../constants';

export default function useFormState({ getAuthHeader }) {
  const urlPrefillDataRef = useRef(null);
  const urlResumeRef = useRef(null);

  const [identite, setIdentite] = useState({
    qualite: 'particulier', civilite: '', nom: '', prenom: '',
    raison_sociale: '', siret: '', forme_juridique: '', code_naf: '',
    representant_nom: '', representant_qualite: '',
    adresse: '', code_postal: '', ville: '', pays: 'France',
    telephone: '', email: '',
  });

  const [operation, setOperation] = useState({
    type_operation: 'construction_neuve', type_batiment: 'individuel',
    territoire: 'guadeloupe', destination_operation: 'Habitation',
    cout_global_ttc: '', adresse_chantier: '', code_postal_chantier: '',
    ville_chantier: '', pays_chantier: 'France',
    reference_cadastrale: '', reference_permis: '', date_permis: '',
    commune_delivrance_permis: '', description_projet: '',
    date_reception_pv: '',
  });

  const [technique, setTechnique] = useState({
    cout_construction_ht: 150000, surface_plancher: 100,
    nombre_batiments: 1, nombre_logements: 1,
    nombre_locaux_professionnels: 0, nombre_niveaux: 1, nombre_sous_sols: 0,
    date_debut_travaux: '', duree_travaux_mois: 12,
    type_structure: 'beton', type_fondations: 'superficielles',
    presence_sous_sol: false, presence_piscine: false, surface_piscine: '',
    mesures_prevention: false, part_entreprises_ess: 0,
  });

  const [etudes, setEtudes] = useState({
    etude_sol_realisee: false, nom_bureau_etude_sol: '',
    etude_assainissement: false, nom_bureau_assainissement: '',
    maitre_oeuvre_architecte: false, nom_architecte: '',
    numero_ordre_architecte: '',
    bureau_etudes_techniques: false, nom_bureau_etudes_structure: '',
  });

  const [intervenants, setIntervenants] = useState([{
    id: 1, raison_sociale: '', siret: '', role: 'entreprise_generale',
    assurance_compagnie: '', assurance_date_effet: '',
    assurance_numero_police: '', assurance_reference_solidaire: ''
  }]);

  const addIntervenant = () => {
    setIntervenants(prev => [...prev, {
      id: Date.now(), raison_sociale: '', siret: '', role: '',
      assurance_compagnie: '', assurance_date_effet: '',
      assurance_numero_police: '', assurance_reference_solidaire: ''
    }]);
  };

  const removeIntervenant = (id) => {
    if (intervenants.length > 1) setIntervenants(prev => prev.filter(i => i.id !== id));
  };

  const updateIntervenant = (id, field, value) => {
    setIntervenants(prev => prev.map(i => i.id === id ? { ...i, [field]: value } : i));
  };

  const isPersonneMorale = useMemo(() =>
    ['sci', 'promoteur', 'bailleur_social', 'collectivite', 'entreprise', 'cooperative'].includes(identite.qualite),
    [identite.qualite]
  );

  const restoreDraftData = (draft) => {
    if (draft.identite && typeof draft.identite === 'object') setIdentite(prev => ({ ...prev, ...draft.identite }));
    if (draft.operation && typeof draft.operation === 'object') setOperation(prev => ({ ...prev, ...draft.operation }));
    if (draft.technique && typeof draft.technique === 'object') setTechnique(prev => ({ ...prev, ...draft.technique }));
    if (draft.etudes && typeof draft.etudes === 'object') setEtudes(prev => ({ ...prev, ...draft.etudes }));
    if (draft.intervenants && Array.isArray(draft.intervenants) && draft.intervenants.length > 0) {
      const withIds = draft.intervenants.map((inv, idx) => ({
        ...inv,
        id: inv.id != null ? inv.id : Date.now() + idx,
      }));
      setIntervenants(withIds);
    }
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const resumeRef = searchParams.get('resume');
    if (resumeRef && !urlResumeRef.current) {
      urlResumeRef.current = true;
      setTimeout(async () => {
        try {
          const response = await axios.get(
            `${BACKEND_URL}/api/domium/subscribe/draft/${resumeRef}/resume`,
            { headers: getAuthHeader() }
          );
          if (response.data.success && response.data.draft) {
            restoreDraftData(response.data.draft);
            toast.success(`Simulation reprise - ${response.data.days_remaining || 30} jours restants`);
          } else {
            toast.error('Donnees de simulation incompletes');
          }
        } catch {
          toast.error('Impossible de reprendre cette simulation');
        }
      }, 500);
      return;
    }
    if (searchParams.get('prefill') === 'true' && !urlPrefillDataRef.current) {
      urlPrefillDataRef.current = true;

      // V37.67 — Prefill CCMI complet via sessionStorage (donnees riches)
      const ccmiKey = searchParams.get('from_ccmi');
      if (ccmiKey) {
        try {
          const raw = sessionStorage.getItem(`domium_prefill_${ccmiKey}`);
          if (raw) {
            const ccmiPrefill = JSON.parse(raw);
            if (ccmiPrefill.identite) setIdentite(prev => ({ ...prev, ...ccmiPrefill.identite }));
            if (ccmiPrefill.operation) setOperation(prev => ({ ...prev, ...ccmiPrefill.operation }));
            if (ccmiPrefill.technique) setTechnique(prev => ({ ...prev, ...ccmiPrefill.technique }));
            if (Array.isArray(ccmiPrefill.intervenants) && ccmiPrefill.intervenants.length > 0) {
              setIntervenants(ccmiPrefill.intervenants.map((inv, idx) => ({ ...inv, id: idx + 1 })));
            }
            sessionStorage.removeItem(`domium_prefill_${ccmiKey}`);
            const ref = searchParams.get('ccmi_ref') || ccmiKey.slice(0, 8);
            toast.success(`Simulateur pre-rempli depuis le projet CCMI ${ref}`);
            return;
          }
        } catch {
          // Fallback URL params si sessionStorage echoue
        }
      }

      const p = {
        nom: searchParams.get('nom') || '', prenom: searchParams.get('prenom') || '',
        raison_sociale: searchParams.get('raison_sociale') || '',
        siret: searchParams.get('siret') || '', email: searchParams.get('email') || '',
        telephone: searchParams.get('telephone') || '',
        adresse: searchParams.get('adresse') || '',
        territoire: searchParams.get('territoire') || 'guadeloupe',
      };
      setIdentite(prev => ({
        ...prev, nom: p.nom, prenom: p.prenom, raison_sociale: p.raison_sociale,
        siret: p.siret, email: p.email, telephone: p.telephone, adresse: p.adresse,
        qualite: p.siret ? 'entreprise' : 'particulier',
      }));
      const tMap = { 'Guadeloupe': 'guadeloupe', 'Martinique': 'martinique', 'Guyane': 'guyane', 'La Reunion': 'reunion', 'Mayotte': 'mayotte' };
      setOperation(prev => ({ ...prev, territoire: tMap[p.territoire] || p.territoire?.toLowerCase() || 'guadeloupe' }));
      toast.success(`Donnees pre-remplies pour ${p.prenom} ${p.nom}`);
    }
  }, []);

  return {
    identite, setIdentite, operation, setOperation,
    technique, setTechnique, etudes, setEtudes,
    intervenants, setIntervenants,
    addIntervenant, removeIntervenant, updateIntervenant,
    isPersonneMorale, restoreDraftData,
  };
}
